import { Fragment, useCallback, useContext, useMemo } from 'react';
import ReactFlow, { addEdge, Background } from 'reactflow';
import { useSelector } from 'react-redux';
import HeaderNode from '../custom-nodes/header';
import { WorkflowContext } from 'providers/workflow-provider';
import { Checkbox, ContentWrapper, SelectMenu, SideDrawer, Textbox, SideHelpIcon } from 'components/generic';
import { workflowComponentList, linkConfigurationKeys, linkConfigurationStructure, apiOp, tablist } from 'config';
import { motion } from 'framer-motion';
import { cloneDeep } from 'lodash';
import { getValueMtpl, getValuesById, textFormatter } from 'utils';
import DecisionBlock from 'components/generic/decision-block';
import actionNode from '../custom-nodes/action';
import delayNode from '../custom-nodes/delay';
import DecisionNode from '../custom-nodes/decision';
import classNames from 'classnames';
import Images from 'assets/Images';
import { useFetch } from 'hooks';
import 'reactflow/dist/style.css';
import './style.scss';

const nodeTypes = { header: HeaderNode, action: actionNode, delay: delayNode, decision: DecisionNode };

const WorkflowConfiguration = props => {
	const context = useContext(WorkflowContext);
	const {
		initObj,
		createObj,
		dockerState,
		selectedNode,
		setNodes,
		setInitObj,
		setCreateObj,
		setNodeFlow,
		entryConditions,
		exitConditions,
		componentState,
		setFlowInstance,
		nodes,
		setComponentState,
		onNodesChange,
		edges,
		wfConditions,
		templateList,
		setEdges,
		nodeConfigureIndex,
		setNodeConfigureIndex,
		onEdgesChange,
		handleComponentClick,
		setConditionType,
		setWfConditions
	} = context;
	const api = useFetch();

	const { referenceData, factList, clientData } = useSelector(state => ({ referenceData: state?.referenceData?.data, factList: state?.factList?.data, clientData: state?.clientData?.data }));

	const paymentOptions = useMemo(() => {
		const data = getValuesById(referenceData, 'rd_paymentOptions')?.at()?.value;
		return data?.filter(el => clientData?.c_paymentDetails?.c_paymentOptions?.includes(el?.key));
	}, [referenceData]);

	const onConnect = useCallback(params => setEdges(eds => addEdge(params, eds)), []);
	const onInit = el => setFlowInstance(el);

	const expressionList = useMemo(() => factList?.filter(el => el?.fa_formula_type === 'FORMULA'), [factList]);
	const decisionTypesList = useMemo(() => getValuesById(referenceData, 'rd_decisionTypes')?.at()?.value, [referenceData]);
	const yesNoList = useMemo(() => getValuesById(referenceData, 'rd_yesNoCheck')?.at()?.value, [referenceData]);
	const timePeriodList = useMemo(() => getValuesById(referenceData, 'rd_timePeriod')?.at()?.value, [referenceData]);
	const timeUnitsList = useMemo(() => getValuesById(referenceData, 'rd_timeUnits')?.at()?.value, [referenceData]);
	const paymentFrequencyList = useMemo(() => getValuesById(referenceData, 'rd_paymentFrequency')?.at()?.value, [referenceData]);
	const workflowActionList = useMemo(() => getValuesById(referenceData, 'rd_workflowActions')?.at()?.value, [referenceData]);
	const communicationAtList = useMemo(() => getValuesById(referenceData, 'rd_communicationAt')?.at()?.value, [referenceData]);
	const displayFeatures = useMemo(() => getValuesById(referenceData, 'rd_dssDisplayFeatures')?.at()?.value, [referenceData]);
	const callbackLaterSlots = useMemo(() => getValuesById(referenceData, 'rd_callbackSlots')?.at()?.value, [referenceData]);
	const dataTypeList = useMemo(() => getValuesById(referenceData, 'rd_dataType')?.at()?.value, [referenceData]);
	const operatorList = useMemo(() => getValuesById(referenceData, 'rd_factsOperatorList')?.at()?.value, [referenceData]);
	const hardshipTypes = useMemo(() => getValuesById(referenceData, 'rd_hardshipTypes')?.at()?.value, [referenceData]);
	const languageList = useMemo(() => getValuesById(referenceData, 'rd_languageList')?.at()?.value, [referenceData]);
	const templateDropdownList = useMemo(() => templateList?.map(el => ({ key: el?.templateId, value: el?.templateName })), [templateList]);

	const currentNodeData = useMemo(() => initObj?.wf_nodes?.[nodeConfigureIndex]?.data, [initObj?.wf_nodes, nodeConfigureIndex]);

	const handleCompClick = el => {
		if (componentState?.nextNode?.includes(el) && dockerState !== 'view') {
			const { data, id } = selectedNode;
			const index = createObj?.wf_nodes?.length;
			const position = { x: data?.position?.x, y: data?.position?.y + 200 };
			const obj = { id: index?.toString(), type: el, position, data: { type: el, position } };
			setNodeFlow(obj, index, id);
			setNodeConfigureIndex(index);
			handleComponentClick(el);
		}
	};

	const mainComponents = useCallback(
		() => (
			<ul className={classNames({ workflow__components: true })}>
				{workflowComponentList?.map((el, i) => (
					<li key={i} className={classNames({ 'space-between': true, 'workflow__components--disable': !componentState?.nextNode?.includes(el?.key) })} onClick={() => handleCompClick(el?.key)}>
						{el?.value} <img src={Images.arrow} alt='arrow' />
					</li>
				))}
			</ul>
		),
		[workflowComponentList, componentState]
	);

	const handleActionData = ({ e, key, subKey, index }) => {
		dockerState !== 'view' &&
			setInitObj(prevState => {
				const state = cloneDeep(prevState);
				const item = state?.wf_nodes?.filter(el => el?.id === nodeConfigureIndex?.toString());
				item[0].data.actionList ??= [];
				item[0].data.linkConfiguration ??= {};
				if (key === 'featureList') {
					const linkConfiguration = cloneDeep(linkConfigurationStructure);
					linkConfigurationKeys.forEach(linkConfigKey =>
						!e?.includes(linkConfigKey.key) ? delete item[0].data.linkConfiguration[linkConfigKey.value] : (item[0].data.linkConfiguration[linkConfigKey.value] ??= linkConfiguration[linkConfigKey.value])
					);
					(item[0].data.linkConfiguration ??= {})[key] = displayFeatures.filter(elmn => e?.includes(elmn.key));
				} else if (key === 'template') {
					item[0].data.actionList[index].templateId = e;
				} else if (['to', 'from'].includes(key)) {
					item[0].data.linkConfiguration[subKey].timeslots[index][key] = e;
				} else if (['templateId', 'templateLanguage'].includes(key)) {
					item[0].data.linkConfiguration[subKey].termsAndConditionsTemplate[index][key] = e;
				} else if (key === 'termsAndConditionsTemplateAdd') {
					(item[0].data.linkConfiguration[subKey].termsAndConditionsTemplate ??= []).push({ templateId: '', templateLanguage: '' });
				} else if (key === 'callbackAddSlot') {
					(item[0].data.linkConfiguration.callbackLaterConfiguration.timeslots ??= []).push({ from: '', to: '' });
				} else if (['actionList'].includes(key)) {
					if (e?.includes('PREFERRED_CHANNEL')) {
						item[0].data[key] = [{ type: 'PREFERRED_CHANNEL', templateId: '' }];
					} else {
						item[0].data[key] = e?.map(el => ({ type: el, templateId: '' }));
					}
				} else if (subKey) {
					(item[0].data.linkConfiguration[subKey] ??= {})[key] = e;
				} else if (key === 'sendCommunicationAt') {
					item[0].data[key] = e;
				} else {
					(item[0].data.linkConfiguration ??= {})[key] = e;
				}
				setNodes(state?.wf_nodes);
				return state;
			});
	};

	const handleDelayDecisionData = (e, key, type) => {
		dockerState !== 'view' &&
			setInitObj(prevState => {
				const state = cloneDeep(prevState);
				const item = state?.wf_nodes?.filter(el => el?.id === nodeConfigureIndex?.toString());
				if (type === 'delay') {
					(item[0].data.duration ??= {})[key] = e;
				} else {
					item[0].data.action = e;
				}
				setNodes(state?.wf_nodes);
				return state;
			});
	};

	const handleLinkConfigurationClick = () => {
		handleComponentClick('linkConfiguration');
	};

	const handleCallbackDelete = i => {
		setInitObj(prevState => {
			const state = cloneDeep(prevState);
			const item = state?.wf_nodes?.filter(el => el?.id === nodeConfigureIndex?.toString());
			item[0].data.linkConfiguration.callbackLaterConfiguration.timeslots.splice(i, 1);
			return state;
		});
	};

	const handlePaynowTemplateDelete = (i, type) => {
		setInitObj(prevState => {
			const state = cloneDeep(prevState);
			const item = state?.wf_nodes?.filter(el => el?.id === nodeConfigureIndex?.toString());
			item[0].data.linkConfiguration[type].termsAndConditionsTemplate.splice(i, 1);
			return state;
		});
	};

	const actionComponents = useCallback(
		() => (
			<>
				<div className='row'>
					<div className='col-12'>
						<SelectMenu
							multiple
							mandatory
							infoContent='Select Action Type'
							label='Action'
							placeholder='Select'
							disabled={dockerState === 'view'}
							value={getValueMtpl(workflowActionList, currentNodeData?.actionList, 'type')}
							onChange={e => handleActionData({ e, key: 'actionList' })}
							menuOptions={workflowActionList}
						/>
					</div>
				</div>
				{currentNodeData?.actionList?.map((el, i) => (
					<div key={i} className='row'>
						<div className='col-12'>
							<SelectMenu
								infoContent='Select template'
								label={`Template for ${textFormatter(el?.type)}`}
								placeholder='Select'
								disabled={dockerState === 'view'}
								value={getValuesById(templateDropdownList, el?.templateId)}
								mandatory
								onChange={e => handleActionData({ e, key: 'template', index: i })}
								menuOptions={templateDropdownList}
							/>
						</div>
					</div>
				))}
				<div className='row'>
					<div className='col-12'>
						<SelectMenu
							mandatory
							infoContent='Select communication at'
							label='Communication At'
							placeholder='Select'
							disabled={dockerState === 'view'}
							value={getValuesById(communicationAtList, currentNodeData?.sendCommunicationAt)}
							onChange={e => handleActionData({ e, key: 'sendCommunicationAt' })}
							menuOptions={communicationAtList}
						/>
					</div>
				</div>
				<div className='display-flex'>
					<span className='hyperlink' onClick={handleLinkConfigurationClick}>
						Link configuration
					</span>
					<SideHelpIcon infoContent='Link configurations for the action node' />
				</div>

			</>
		),
		[workflowActionList, initObj, templateDropdownList, nodeConfigureIndex, dockerState]
	);

	const hardshipComponent = useMemo(
		() => (
			<ContentWrapper border>
				<h4>Hardship</h4>

				<div className='row'>
					<div className='col-6'>
						<SelectMenu
							multiple
							mandatory
							infoContent='Enter Hardship Reason'
							label='Hardship Reason'
							disabled={dockerState === 'view'}
							placeholder='Select'
							value={getValueMtpl(hardshipTypes, currentNodeData?.linkConfiguration?.hardshipConfiguration?.categoryList)}
							onChange={e => handleActionData({ e, key: 'categoryList', subKey: 'hardshipConfiguration' })}
							menuOptions={hardshipTypes}
						/>
					</div>
					<div className='col-6'>
						<Textbox
							type='number'
							mandatory
							infoContent='Enter char limit'
							label='Set Maximum Char limit'
							disabled={dockerState === 'view'}
							placeholder='Enter a number'
							value={currentNodeData?.linkConfiguration?.hardshipConfiguration?.maximumCharLimit}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'maximumCharLimit', subKey: 'hardshipConfiguration' })}
						/>
					</div>
				</div>
			</ContentWrapper>
		),
		[initObj, templateDropdownList, paymentOptions, nodeConfigureIndex, dockerState]
	);

	const paynowComponent = useMemo(
		() => (
			<ContentWrapper border>
				<h4>Pay Now</h4>

				<div className='row'>
					<div className='col-6'>
						<Textbox
							type='number'
							infoContent='Enter min amount'
							label='Set minimum amount'
							placeholder='Enter a number'
							disabled={dockerState === 'view'}
							value={currentNodeData?.linkConfiguration?.paynowConfiguration?.minimumAmount}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'minimumAmount', subKey: 'paynowConfiguration' })}
						/>
					</div>
					<div className='col-6'>
						<Textbox
							type='number'
							infoContent='Enter max amount'
							label='Set maximum amount'
							placeholder='Enter a number'
							disabled={dockerState === 'view'}
							value={currentNodeData?.linkConfiguration?.paynowConfiguration?.maximumAmount}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'maximumAmount', subKey: 'paynowConfiguration' })}
						/>
					</div>
				</div>
				<div className='row'>
					<div className='col-6'>
						<SelectMenu
							multiple
							infoContent='Select'
							label='Select payment gateways'
							placeholder='Select'
							disabled={dockerState === 'view'}
							value={getValueMtpl(paymentOptions, currentNodeData?.linkConfiguration?.paynowConfiguration?.paymentGateways)}
							mandatory
							onChange={e => handleActionData({ e, key: 'paymentGateways', subKey: 'paynowConfiguration' })}
							menuOptions={paymentOptions}
						/>
					</div>
				</div>
				<Checkbox
					lightWeight
					checked={currentNodeData?.linkConfiguration?.paynowConfiguration?.allowEdit}
					disabled={dockerState === 'view'}
					onClick={e => handleActionData({ e: e?.target?.checked, key: 'allowEdit', subKey: 'paynowConfiguration' })}
					content='Allow the customer to edit payment amount'
				/>
				{currentNodeData?.linkConfiguration?.paynowConfiguration?.termsAndConditionsTemplate?.map((el, i) => (
					<div key={i} className='row relative'>
						<div className='col-6'>
							<SelectMenu
								label='Select Language'
								infoContent='Select Language'
								disabled={dockerState === 'view'}
								placeholder='Select Language'
								value={getValuesById(languageList, el?.templateLanguage)}
								onChange={e => handleActionData({ e, key: 'templateLanguage', subKey: 'paynowConfiguration', index: i })}
								menuOptions={languageList}
							/>
						</div>
						<div className='col-6'>
							<SelectMenu
								label='Select Template'
								infoContent='Select Template'
								placeholder='Select Template'
								disabled={dockerState === 'view'}
								value={getValuesById(templateDropdownList, el?.templateId)}
								onChange={e => handleActionData({ e, key: 'templateId', subKey: 'paynowConfiguration', index: i })}
								menuOptions={templateDropdownList}
							/>
						</div>
						<div className='workflow-config__delete-img'>
							<img onClick={() => handlePaynowTemplateDelete(i, 'paynowConfiguration')} src={Images.closeBlue} width='8px' height='8px' alt='delete' />
						</div>
					</div>
				))}
				{dockerState !== 'view' && (
					<div className='workflow-config__timeslotAdd'>
						<img onClick={() => handleActionData({ key: 'termsAndConditionsTemplateAdd', subKey: 'paynowConfiguration' })} src={Images.add} alt='add' width='25px' height='25px' />
						<span>Add Terms and Conditions</span>
					</div>
				)}
			</ContentWrapper>
		),
		[initObj, templateDropdownList, paymentOptions, nodeConfigureIndex, dockerState]
	);

	const paymentPlanComponent = useMemo(
		() => (
			<ContentWrapper border>
				<h4>Payment Plan</h4>

				<div className='row'>
					<div className='col-6'>
						<Textbox
							type='number'
							mandatory
							infoContent='Enter Max Char limit'
							label='Set Maximum Char limit'
							disabled={dockerState === 'view'}
							placeholder='Enter a number'
							value={currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.maximumCharLimit}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'maximumCharLimit', subKey: 'paymentPlanConfiguration' })}
						/>
					</div>
				</div>
				<div className='row'>
					<div className='col-6'>
						<SelectMenu
							multiple
							mandatory
							infoContent='Select Frequencies'
							label='Select Payment Frequencies'
							disabled={dockerState === 'view'}
							placeholder='Select'
							value={getValueMtpl(paymentFrequencyList, currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.paymentFrequency)}
							onChange={e => handleActionData({ e, key: 'paymentFrequency', subKey: 'paymentPlanConfiguration' })}
							menuOptions={paymentFrequencyList}
						/>
					</div>
					<div className='col-6'>
						<Textbox
							type='number'
							mandatory
							infoContent='Enter min amount'
							label='Set minimum amount'
							placeholder='Enter a number'
							disabled={dockerState === 'view'}
							value={currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.minimumAmount}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'minimumAmount', subKey: 'paymentPlanConfiguration' })}
						/>
					</div>
				</div>
				<div className='row'>
					<div className='col-6'>
						<Checkbox
							lightWeight
							checked={currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.allowEdit}
							disabled={dockerState === 'view'}
							onClick={e => handleActionData({ e: e?.target?.checked, key: 'allowEdit', subKey: 'paymentPlanConfiguration' })}
							content='Allow edit for Payment Plan'
						/>
					</div>
					<div className='col-6'>
						<Checkbox
							lightWeight
							checked={currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.captureReason}
							disabled={dockerState === 'view'}
							onClick={e => handleActionData({ e: e?.target?.checked, key: 'captureReason', subKey: 'paymentPlanConfiguration' })}
							content='Capture Reason for Payment Plan'
						/>
					</div>
				</div>
				{currentNodeData?.linkConfiguration?.paymentPlanConfiguration?.termsAndConditionsTemplate?.map((el, i) => (
					<div key={i} className='row relative'>
						<div className='col-6'>
							<SelectMenu
								label='Select Language'
								infoContent='Select Language'
								disabled={dockerState === 'view'}
								placeholder='Select Language'
								value={getValuesById(languageList, el?.templateLanguage)}
								onChange={e => handleActionData({ e, key: 'templateLanguage', subKey: 'paymentPlanConfiguration', index: i })}
								menuOptions={languageList}
							/>
						</div>
						<div className='col-6'>
							<SelectMenu
								label='Select Template'
								infoContent='Select Template'
								placeholder='Select Template'
								disabled={dockerState === 'view'}
								value={getValuesById(templateDropdownList, el?.templateId)}
								onChange={e => handleActionData({ e, key: 'templateId', subKey: 'paymentPlanConfiguration', index: i })}
								menuOptions={templateDropdownList}
							/>
						</div>
						<div className='workflow-config__delete-img'>
							<img onClick={() => handlePaynowTemplateDelete(i, 'paymentPlanConfiguration')} src={Images.closeBlue} width='8px' height='8px' alt='delete' />
						</div>
					</div>
				))}
				{dockerState !== 'view' && (
					<div className='workflow-config__timeslotAdd'>
						<img onClick={() => handleActionData({ key: 'termsAndConditionsTemplateAdd', subKey: 'paymentPlanConfiguration' })} src={Images.add} alt='add' width='25px' height='25px' />
						<span>Add Terms and Conditions</span>
					</div>
				)}
			</ContentWrapper>
		),
		[initObj, nodeConfigureIndex, yesNoList, paymentFrequencyList, templateDropdownList, dockerState]
	);

	const callbackLaterComponent = useMemo(
		() => (
			<ContentWrapper border>
				<h4>Call Back Later</h4>
				<div className='row'>
					<div className='col-6'>
						<Textbox
							type='number'
							infoContent='Enter Maximum Char limit'
							label='Set Maximum Char limit'
							disabled={dockerState === 'view'}
							placeholder='Enter a number'
							value={currentNodeData?.linkConfiguration?.callbackLaterConfiguration?.maximumCharLimit}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'maximumCharLimit', subKey: 'callbackLaterConfiguration' })}
						/>
					</div>
				</div>
				<h4>Timeslots</h4>
				{currentNodeData?.linkConfiguration?.callbackLaterConfiguration?.timeslots.map((el, i) => (
					<div key={i} className='row relative'>
						<div className='col-6'>
							<SelectMenu
								label='From'
								infoContent='Select from'
								disabled={dockerState === 'view'}
								placeholder='Select'
								value={getValuesById(callbackLaterSlots, el?.from)}
								onChange={e => handleActionData({ e, key: 'from', subKey: 'callbackLaterConfiguration', index: i })}
								menuOptions={callbackLaterSlots}
							/>
						</div>
						<div className='col-6'>
							<SelectMenu
								label='To'
								infoContent='Select to'
								placeholder='Select'
								disabled={dockerState === 'view'}
								value={getValuesById(callbackLaterSlots, el?.to)}
								onChange={e => handleActionData({ e, key: 'to', subKey: 'callbackLaterConfiguration', index: i })}
								menuOptions={callbackLaterSlots}
							/>
						</div>
						<div className='workflow-config__delete-img'>
							<img onClick={() => handleCallbackDelete(i)} src={Images.closeBlue} width='8px' height='8px' alt='delete' />
						</div>
					</div>
				))}
				{dockerState !== 'view' && (
					<div className='workflow-config__timeslotAdd'>
						<img onClick={() => handleActionData({ key: 'callbackAddSlot' })} src={Images.add} alt='add' width='25px' height='25px' />
						<span>Add row</span>
					</div>
				)}
				<div className='row'>
					<div className='col-6'>
						<Checkbox
							lightWeight
							checked={currentNodeData?.linkConfiguration?.callbackLaterConfiguration?.allowEdit}
							disabled={dockerState === 'view'}
							onClick={e => handleActionData({ e: e?.target?.checked, key: 'allowEdit', subKey: 'callbackLaterConfiguration' })}
							content='Allow edit for callback later'
						/>
					</div>
					<div className='col-6'>
						<Checkbox
							lightWeight
							checked={currentNodeData?.linkConfiguration?.callbackLaterConfiguration?.captureReason}
							disabled={dockerState === 'view'}
							onClick={e => handleActionData({ e: e?.target?.checked, key: 'captureReason', subKey: 'callbackLaterConfiguration' })}
							content='Capture Reason for Call Back Later'
						/>
					</div>
				</div>
			</ContentWrapper>
		),
		[initObj, nodeConfigureIndex, callbackLaterSlots, dockerState]
	);

	const decisionComponent = useCallback(
		() => (
			<SelectMenu
				label='Activity type'
				placeholder='Select'
				infoContent='Select Activity type'
				disabled={dockerState === 'view'}
				value={getValuesById(decisionTypesList, currentNodeData?.action)}
				mandatory
				onChange={e => handleDelayDecisionData(e, 'action', 'decision')}
				menuOptions={decisionTypesList}
			/>
		),
		[initObj, decisionTypesList, nodeConfigureIndex, dockerState]
	);

	const linkConfigurationComponents = useCallback(() => {
		const featureList = currentNodeData?.linkConfiguration?.featureList;
		return (
			<>
				<div className='row'>
					<div className='col-6'>
						<SelectMenu
							multiple
							infoContent='Select feature'
							label='Select the features to be displayed in DSS link'
							placeholder='Select'
							disabled={dockerState === 'view'}
							value={getValueMtpl(
								displayFeatures,
								currentNodeData?.linkConfiguration?.featureList?.map(el => el?.key)
							)}
							mandatory
							onChange={e => handleActionData({ e, key: 'featureList' })}
							menuOptions={displayFeatures}
						/>
					</div>
					<div className='col-6'>
						<Textbox
							infoContent='Enter days'
							type='number'
							disabled={dockerState === 'view'}
							label='No of days DSS link is active'
							placeholder='Enter a number'
							value={currentNodeData?.linkConfiguration?.activeTill}
							onChange={e => handleActionData({ e: e?.target?.value, key: 'activeTill' })}
						/>
					</div>
				</div>

				{featureList?.map((el, i) => (
					<Fragment key={i}>
						{el?.key === 'PAY_NOW' && paynowComponent}
						{el?.key === 'PAYMENT_PLAN' && paymentPlanComponent}
						{el?.key === 'CALLBACK_LATER' && callbackLaterComponent}
						{el?.key === 'HARDSHIP' && hardshipComponent}
					</Fragment>
				))}
			</>
		);
	}, [displayFeatures, initObj, nodeConfigureIndex, dockerState]);

	const handleConditionCheckbox = (e, el, type) => {
		setCreateObj(prevState => {
			let state = cloneDeep(prevState);
			if (state?.wf_conditions) {
				state.wf_conditions[type] = e ? el?.wfc_id : null;
			}
			return state;
		});
		setTimeout(() => setComponentState(null), 100);
	};

	const handleCreateRule = method => {
		setConditionType({ method, type: componentState?.currentStage });
		method === 'add' && setWfConditions({});
		handleComponentClick(method === 'add' ? 'addCondition' : 'editCondition');
	};

	const handleConditionImgClick = async (el, type) => {
		type === 'delete' && props.deleteWfc(el?.wfc_id);
		if (type === 'update') {
			const { data } = await api({ url: `workflow/v1/condition/${el?.wfc_id}`, method: 'get' });
			setWfConditions(data?.data?.at());
			handleCreateRule('update');
		}
	};

	const conditionsComponent = () => {
		const conditionList = componentState?.currentStage === 'entryCondition' ? entryConditions : exitConditions;

		return (
			<>
				<ul>
					{conditionList?.map((el, i) => (
						<li className='workflow-config__conditions space-between' key={i}>
							<Checkbox
								last
								lightWeight
								type='radio'
								disabled={dockerState === 'view'}
								content={el?.wfc_name}
								checked={createObj?.wf_conditions?.[componentState?.currentStage] === el?.wfc_id}
								onClick={e => handleConditionCheckbox(e?.target?.checked, el, componentState?.currentStage)}
							/>
							<div className='flex-start workflow-config__icons'>
								<img onClick={() => handleConditionImgClick(el, 'update')} src={Images['edit-blue']} />
								<img onClick={() => handleConditionImgClick(el, 'delete')} src={Images.deleteBlue} />
							</div>
						</li>
					))}
				</ul>
				{dockerState !== 'view' && (
					<div className='display-flex'>
						<span className='hyperlink workflow-config__customRule' onClick={() => handleCreateRule('add')}>
							Create custom rule
						</span>
						<SideHelpIcon infoContent='Create custom rules for the accounts' />
					</div>

				)}
			</>
		);
	};

	const delayComponents = () => {
		return (
			<>
				<SelectMenu
					mandatory
					infoContent='Select Unit of time period'
					label='Unit of time period'
					placeholder='Select'
					disabled={dockerState === 'view'}
					value={getValuesById(timeUnitsList, currentNodeData?.duration?.period)}
					onChange={e => handleDelayDecisionData(e, 'period', 'delay')}
					menuOptions={timeUnitsList}
				/>
				<SelectMenu
					infoContent='Select duration'
					mandatory
					label='Duration of time period'
					placeholder='Select'
					disabled={dockerState === 'view'}
					value={getValuesById(timePeriodList, currentNodeData?.duration?.value)}
					onChange={e => handleDelayDecisionData(e, 'value', 'delay')}
					menuOptions={timePeriodList}
				/>
			</>
		);
	};

	const addConditionComponent = () => {
		return (
			<>
				<DecisionBlock
					rule={wfConditions?.wfc_rules}
					title={createObj?.wf_description}
					state={dockerState}
					viewOnly={dockerState === 'view'}
					listHeight={{ height: 'calc(100vh - 315px)' }}
					rulesHeight={{ height: 'calc(100vh - 220px)' }}
					factListEvent={factList}
					expressionList={expressionList}
					operatorListEvent={apiOp}
					tablist={tablist}
					operators={operatorList}
					dataType={dataTypeList}
					onCreateFact={handleCreateFact}
					onCreateExpression={handleCreateExpression}
					onSaveRule={handleRuleSave}
				/>
			</>
		);
	};

	const sidebarRenderer = () => {
		const obj = {
			components: () => mainComponents(),
			action: () => actionComponents(),
			delay: () => delayComponents(),
			decision: () => decisionComponent(),
			linkConfiguration: () => linkConfigurationComponents(),
			entryCondition: () => conditionsComponent(),
			exitCondition: () => conditionsComponent(),
			addCondition: () => addConditionComponent(),
			editCondition: () => addConditionComponent(),
			DEFAULT: () => null
		};
		return (obj[componentState?.currentStage] || obj['DEFAULT'])();
	};

	const handleCreateFact = async e => {
		await api({ url: 'factManagement/v1/addFact', method: 'post', data: e, successToaster: true });
	};

	const handleCreateExpression = async e => {
		const data = { fa_name: e?.name, fa_formula_type: 'FORMULA', fa_formula: e?.formula, fa_fields: e?.fields };
		await api({ url: 'factManagement/v1/addFact', method: 'post', data, successToaster: true });
	};

	const handleRuleSave = e => {
		props.handleSaveRule(e);
	};

	const onCloseSidebar = e => {
		if (['action', 'delay', 'decision', 'linkConfiguration'].includes(componentState?.currentStage)) {
			if (e) setCreateObj(initObj)
			else {
				setNodes(createObj?.wf_nodes);
				setInitObj(createObj);
			}
		}
		setComponentState(null);
	}

	return (
		<>
			<div className='workflow-config'>
				<ReactFlow
					nodes={nodes}
					nodesConnectable={false}
					edges={edges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					onConnect={onConnect}
					nodesDraggable={false}
					onInit={onInit}
					panOnScroll
					fitView
					attributionPosition='top-right'
					nodeTypes={nodeTypes}
				>
					{/* <Controls showInteractive={false} /> */}
					<Background color='#aaa' gap={16} />
				</ReactFlow>
				<SideDrawer drawerState={componentState} title={componentState ? componentState['sidebarHeader'] : ''} showSave={['action', 'delay', 'decision', 'linkConfiguration'].includes(componentState?.currentStage)} onClose={e => onCloseSidebar(e)}>
					{componentState && (
						<motion.div className={classNames({ workflow__drawer: true, 'scroll-bar': true })} initial={{ width: 0 }} animate={{ width: componentState?.sidebarWidth }}>
							{sidebarRenderer()}
						</motion.div>
					)}
				</SideDrawer>
			</div>
		</>
	);
};

export default WorkflowConfiguration;
